import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react'
import {
  Download,
  Copy,
  FileText,
  Link as LinkIcon,
  Clipboard,
  Share,
  ExternalLink,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from 'lucide-react'
import React, { useState, useEffect, memo, useRef, useMemo, useCallback } from 'react'

// React-PDF imports
import { Document, Page, pdfjs } from 'react-pdf'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Configure PDF.js worker for modern versions
// Use local worker file from public directory for better reliability
pdfjs.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.js'

// --- Data Settings imports ---
import useDataSettings from '@/hooks/useDataSettings'
import { AICommandType, createAICommand, useAICommandState, type AICommand } from '@/utils/aiCommandUtils'
import { copyToClipboard as copyMarkdownToClipboard } from '@/utils/documentUtils'
import styles from '~/styles/NodeResource.module.css'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'

// PDF Field interface for form field handling
interface PDFField {
  name: string
  value: string
  type?: string
}

interface NodeResourcePDFData {
  fileId: string
  filename: string
  url: string
  contentType: string
  size: number
  createdAt: string
  groupId: string | null
  groupName?: string
  blobName?: string
  assignedEmails?: string[] | string
  label?: string
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  isLocked?: boolean
  isMinimized?: boolean
  fileAsMarkdown?: string
  documentRequest?: {
    'sysagent-cmd'?: string
    'sysagent-response'?: string
    processing?: boolean
    [key: string]: any
  }
  minifiedInfo?: Record<string, any>
  // Add dataSettings for type safety
  dataSettings?: any
}

const NodeResourcePDF: React.FC<NodeProps & { data: NodeResourcePDFData }> = (props) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()

  // Track if label has been initialized to prevent infinite loops
  const [labelInitialized, setLabelInitialized] = useState(false)

  // Initialize label from filename in useEffect instead of during render
  useEffect(() => {
    if (
      !labelInitialized &&
      (data.label === undefined || data.label === null || data.label === '' || data.label === 'PDF Resource') &&
      data.filename &&
      data.filename !== 'PDF Resource'
    ) {
      setLabelInitialized(true)
      updateNodeData(id, { ...data, label: data.filename })
    }
  }, [id, data, updateNodeData, labelInitialized])

  // UI state
  const [isLocked, setIsLocked] = useState<boolean>(data.isLocked || false)
  const [isMinimized, setIsMinimized] = useState<boolean>(data.isMinimized || false)
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(data.minimizeLevel || 'normal')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState(data.label || data.filename || 'PDF Resource')
  const [assignedEmails, setAssignedEmails] = useState<string[]>(() => {
    if (Array.isArray(data.assignedEmails)) {
      return data.assignedEmails
    } else if (typeof data.assignedEmails === 'string' && data.assignedEmails) {
      return data.assignedEmails.trim().split(/\s+/).filter(Boolean)
    } else {
      return []
    }
  })
  const [copyFeedback, setCopyFeedback] = useState<string>('')

  // --- Data Settings state ---
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)

  // Ref for markdown preview (for HTML copy)
  const markdownPreviewRef = useRef<HTMLDivElement>(null)

  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [rotation, setRotation] = useState<number>(0)
  const [pdfLoading, setPdfLoading] = useState<boolean>(true)
  const [pdfError, setPdfError] = useState<string | null>(null)
  
  // PDF form fields state
  const [pdfFields, setPdfFields] = useState<PDFField[]>([])
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  // Add local field values for immediate UI feedback
  const [localFieldValues, setLocalFieldValues] = useState<Record<string, string>>({})
  const [isLoadingFields, setIsLoadingFields] = useState<boolean>(false)
  const [hasFields, setHasFields] = useState<boolean>(false)
  
  // Delay state for newly created nodes to handle Azure Blob Storage timing
  const [delayedPdfUrl, setDelayedPdfUrl] = useState<string>('')
  const [isNewlyCreated, setIsNewlyCreated] = useState<boolean>(false)

  // --- Data Settings logic (outgoing only) ---
  // Only expose outgoing data (no inputLinks)
  const outgoingFields = useMemo(() => ({
    url: {
      value: data.url,
      enabled: true,
      label: 'File URL',
      description: 'Direct URL to the PDF file',
      permissions: { readable: true, writable: false },
    },
    fileAsMarkdown: {
      value: data.fileAsMarkdown || '',
      enabled: !!data.fileAsMarkdown,
      label: 'File as Markdown',
      description: 'PDF content converted to Markdown (if available)',
      permissions: { readable: true, writable: false },
    },
    filename: {
      value: data.filename,
      enabled: true,
      label: 'Filename',
      description: 'Name of the PDF file',
      permissions: { readable: true, writable: false },
    },
    contentType: {
      value: data.contentType,
      enabled: true,
      label: 'Content Type',
      description: 'MIME type of the file',
      permissions: { readable: true, writable: false },
    },
    size: {
      value: data.size,
      enabled: true,
      label: 'File Size',
      description: 'Size of the file in bytes',
      permissions: { readable: true, writable: false },
    },
    pdfFields: {
      value: pdfFields,
      enabled: hasFields,
      label: 'PDF Form Fields',
      description: 'Detected form fields in the PDF',
      permissions: { readable: true, writable: false },
    },
    fieldValues: {
      value: fieldValues,
      enabled: hasFields && Object.keys(fieldValues).length > 0,
      label: 'Form Field Values',
      description: 'Current values of the form fields',
      permissions: { readable: true, writable: false },
    },
  }), [data.url, data.fileAsMarkdown, data.filename, data.contentType, data.size, pdfFields, hasFields, fieldValues])

  // Use useDataSettings for outgoing/exposed data only
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
    prepareExposedDataFields,
  } = useDataSettings(
    id,
    data,
    (newData) => updateNodeData(id, { ...data, ...newData }),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )

  // Always keep outgoing/exposedData up to date
  useEffect(() => {
    // Only outgoing/exposedData, no inputLinks
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [], // always empty
      exposedData: outgoingFields,
      availableNodes: [], // not relevant
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (JSON.stringify(dataSettings?.exposedData) !== JSON.stringify(outgoingFields)) {
      setDataSettings(newDataSettings)
      updateNodeData(id, { ...data, dataSettings: newDataSettings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outgoingFields])

  // Memoize PDF options to prevent re-creation on every render
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    withCredentials: false,
  }), [])

  // Memoize PDF URL to prevent unnecessary changes
  const pdfUrl = useMemo(() => {
    if (!data.fileId) return ''
    
    // Use backend proxy for PDFs to avoid CORS issues
    // Determine the backend URL based on current environment
    const currentHost = window.location.hostname
    const currentProtocol = window.location.protocol
    
    let backendUrl: string
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      // Development environment
      backendUrl = `${currentProtocol}//localhost:4040`
    } else {
      // Production environment - assume backend is on same host but different port or path
      backendUrl = `${currentProtocol}//${currentHost}`
      // If the current app is on a specific port, backend might be on different port
      if (window.location.port && window.location.port !== '80' && window.location.port !== '443') {
        // Try to guess backend port (common patterns)
        const frontendPort = parseInt(window.location.port)
        const backendPort = frontendPort === 5173 ? 4040 : frontendPort - 1000 // Common patterns
        backendUrl = `${currentProtocol}//${currentHost}:${backendPort}`
      }
    }
    
    return `${backendUrl}/api/files/${data.fileId}/pdf`
  }, [data.fileId])

  // Check if this is a newly created node and add delay for Azure Blob Storage
  useEffect(() => {
    if (!data.createdAt || !pdfUrl) return
    
    const createdTime = new Date(data.createdAt).getTime()
    const now = Date.now()
    const timeSinceCreation = now - createdTime
    const isRecent = timeSinceCreation < 30000 // Consider "new" if created within 30 seconds
    
    setIsNewlyCreated(isRecent)
    
    if (isRecent) {
      // For newly created nodes, add a 3-second delay before loading PDF
      // This gives Azure Blob Storage time to propagate the file
      console.log('Newly created PDF node detected, delaying PDF load by 3 seconds')
      const timer = setTimeout(() => {
        console.log('Delay completed, loading PDF')
        setDelayedPdfUrl(pdfUrl)
        setIsNewlyCreated(false)
      }, 3000)
      
      return () => clearTimeout(timer)
    } else {
      // For existing nodes, load PDF immediately
      setDelayedPdfUrl(pdfUrl)
    }
  }, [pdfUrl, data.createdAt])

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Copy to clipboard
  const copyToClipboard = (text: string, message: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(message)
      },
      () => {
        toast.error('Failed to copy to clipboard')
      }
    )
  }

  // Copy markdown link
  const copyMarkdownLink = () => {
    const markdownLink = `[${data.filename}](${data.url})`
    copyToClipboard(markdownLink, 'Markdown link copied to clipboard')
  }

  // Download file
  const downloadFile = () => {
    const link = document.createElement('a')
    link.href = data.url
    link.setAttribute('download', data.filename || 'download.pdf')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Initialize minimize level from data
  useEffect(() => {
    setMinimizeLevel(data.minimizeLevel || 'normal')
    setIsMinimized((data.minimizeLevel || 'normal') !== 'normal')
  }, [data.minimizeLevel])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};
    
    if (data.filename) {
      minifiedInfo.filename = data.filename;
    }
    
    if (data.contentType) {
      minifiedInfo['content type'] = data.contentType;
    }
    
    if (data.size) {
      minifiedInfo.size = formatFileSize(data.size);
    }
    
    if (numPages > 0) {
      minifiedInfo.pages = `${numPages} pages`;
    }
    
    if (hasFields && pdfFields.length > 0) {
      minifiedInfo['form fields'] = `${pdfFields.length} fields`;
    }
    
    if (data.fileAsMarkdown?.trim()) {
      minifiedInfo['has content'] = 'Yes';
    }
    
    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);
    
    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo });
    }
  }, [data.filename, data.contentType, data.size, data.fileAsMarkdown, numPages, hasFields, pdfFields.length, id]);

  // PDF loading handlers with useCallback to prevent recreation
  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    console.log('PDF loaded successfully with', numPages, 'pages')
    setNumPages(numPages)
    setPdfLoading(false)
    setPdfError(null)
  }, [])

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('Error loading PDF:', error)
    let errorMessage = 'Failed to load PDF document'
    
    // Provide more specific error messages
    if (error.message.includes('Failed to fetch')) {
      errorMessage = 'Unable to load PDF. Backend server may be unavailable.'
    } else if (error.message.includes('CORS')) {
      errorMessage = 'CORS error loading PDF. Using backend proxy failed.'
    } else if (error.message.includes('NetworkError') || error.message.includes('network')) {
      errorMessage = 'Network error loading PDF. Check your connection.'
    } else if (error.message) {
      errorMessage = `PDF loading error: ${error.message}`
    }
    
    setPdfError(errorMessage)
    setPdfLoading(false)
    
    // Show toast with helpful message
    toast.error('Failed to load PDF viewer. You can still download and view the file externally.')
  }, [])

  const onPageLoadError = useCallback((error: Error) => {
    console.error('Error loading PDF page:', error)
    // Don't show toast for individual page errors, just log them
  }, [])

  // PDF navigation functions
  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages))
  }

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0))
  }

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5))
  }

  const rotateClockwise = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  // Reset PDF view when URL changes
  useEffect(() => {
    if (delayedPdfUrl) {
      setPageNumber(1)
      setScale(1.0)
      setRotation(0)
      setPdfError(null)
      setPdfLoading(true)
    }
  }, [delayedPdfUrl])

  // Node action handlers
  const handleLock = () => {
    const newLockState = !isLocked
    setIsLocked(newLockState)
    updateNodeData(id, { ...data, isLocked: newLockState })
  }

  const handleMinimize = () => {
    const newMinState = !isMinimized
    setIsMinimized(newMinState)
    setMinimizeLevel(newMinState ? 'medium' : 'normal')
    updateNodeData(id, { 
      ...data, 
      isMinimized: newMinState,
      minimizeLevel: newMinState ? 'medium' : 'normal'
    })
  }

  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, { 
      ...data, 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }

  const handleTitleChange = (newTitle: string) => {
    updateNodeData(id, { 
      ...data, 
      label: newTitle, 
      filename: newTitle // keep filename in sync with label
    })
  }

  const handleTitleSave = () => {
    handleTitleChange(editingTitleValue)
    setIsEditingTitle(false)
  }

  // --- AI Command State for NodeResourcePDF ---
  const command = data.documentRequest
  const { isProcessing, statusMessage } = useAICommandState(
    command?.['sysagent-cmd'] ? command as AICommand : null, 
    AICommandType.NODE_RESOURCE_PDF
  )

  // --- Manual trigger for Extract MD ---
  const handleExtractMarkdown = () => {
    if (data.url) {
      updateNodeData(id, {
        ...data,
        documentRequest: createAICommand(AICommandType.NODE_RESOURCE_PDF, { extractionType: 'markdown' })
      })
    } else {
      toast.error('No file URL available to extract Markdown');
    }
  }

  // --- Manual trigger for PDF-to-MD conversion ---
  const handleConvertPdfToMd = () => {
    if (data.url) {
      updateNodeData(id, {
        ...data,
        documentRequest: createAICommand(AICommandType.NODE_RESOURCE_PDF, { extractionType: 'pdf-to-md' })
      })
    } else {
      toast.error('No file URL available for PDF conversion');
    }
  }

  // Copy Markdown to clipboard (like NodeDocument)
  const handleCopyToClipboard = () => {
    if (data.fileAsMarkdown) {
      copyMarkdownToClipboard(data.fileAsMarkdown, markdownPreviewRef, setCopyFeedback)
    }
  }

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = useMemo(() => {
    if (isProcessing || isLoadingFields) return "loading";
    if (statusMessage && statusMessage.toLowerCase().includes("error")) return "error";
    if (pdfError) return "error";
    if (data.fileAsMarkdown || hasFields) return "success";
    return "initial";
  }, [isProcessing, isLoadingFields, statusMessage, pdfError, data.fileAsMarkdown, hasFields]);

  // Detect PDF form fields
  const detectFields = useCallback(async () => {
    if (!data.fileId) return

    setIsLoadingFields(true)
    try {
      const backendUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:4040' 
        : window.location.origin
      
      const response = await fetch(`${backendUrl}/api/files/${data.fileId}/pdf/fields`)
      if (response.ok) {
        const result = await response.json()
        setPdfFields(result.fields || [])
        setHasFields((result.fields || []).length > 0)
        
        // Initialize field values from detected fields
        const initialValues: Record<string, string> = {}
        result.fields?.forEach((field: {name: string, value: string}) => {
          initialValues[field.name] = field.value || ''
        })
        setFieldValues(initialValues)
        // Also initialize local field values to match
        setLocalFieldValues(initialValues)
        
        if (result.fields?.length > 0) {
          toast.success(`Detected ${result.fields.length} form field(s)`)
        } else {
          toast.info('No form fields detected in this PDF')
        }
      } else {
        console.error('Failed to detect fields:', response.statusText)
        toast.error('Failed to detect PDF form fields')
        setPdfFields([])
        setHasFields(false)
      }
    } catch (error) {
      console.error('Error detecting fields:', error)
      toast.error('Error detecting PDF form fields')
      setPdfFields([])
      setHasFields(false)
    } finally {
      setIsLoadingFields(false)
    }
  }, [data.fileId])

  // Update PDF with form field values (editable version)
  const updatePdfFields = useCallback(async () => {
    if (!data.fileId || Object.keys(fieldValues).length === 0) return

    try {
      // Create proper field mappings with field names for form field filling
      const mappings = Object.entries(fieldValues).map(([name, value]) => ({
        name: name,          // Include field name for proper form field filling
        field_name: name,    // Alternative field name key
        value: value,
        page: 0,            // Keep page info for compatibility
        x: 100,             // Keep coordinate info as fallback
        y: 700              // Keep coordinate info as fallback
      }))

      console.log('Sending field mappings for updating:', mappings)

      const backendUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:4040' 
        : window.location.origin
      
      const response = await fetch(`${backendUrl}/api/files/${data.fileId}/pdf/fields/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mappings })  // Changed from { values: fieldValues } to { mappings }
      })

      if (response.ok) {
        // Download the updated PDF
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${data.filename?.replace('.pdf', '') || 'document'}_filled.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        toast.success('PDF updated and downloaded successfully')
      } else {
        const errorText = await response.text()
        console.error('Failed to update PDF:', errorText)
        toast.error('Failed to update PDF with form field values')
      }
    } catch (error) {
      console.error('Error updating PDF fields:', error)
      toast.error('Error updating PDF with form field values')
    }
  }, [data.fileId, data.filename, fieldValues])

  // Flatten PDF with form field values (non-editable version) 
  const flattenPdfFields = useCallback(async () => {
    if (!data.fileId || Object.keys(fieldValues).length === 0) return

    try {
      // Create proper field mappings with field names for form field filling
      const mappings = Object.entries(fieldValues).map(([name, value]) => ({
        name: name,          // Include field name for proper form field filling
        field_name: name,    // Alternative field name key
        value: value,
        page: 0,            // Keep page info for compatibility
        x: 100,             // Keep coordinate info as fallback
        y: 700              // Keep coordinate info as fallback
      }))

      console.log('Sending field mappings for flattening:', mappings)

      const backendUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:4040' 
        : window.location.origin
      
      const response = await fetch(`${backendUrl}/api/files/${data.fileId}/pdf/fields/flatten`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mappings })
      })

      if (response.ok) {
        // Download the flattened PDF
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${data.filename?.replace('.pdf', '') || 'document'}_flattened.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        toast.success('PDF flattened and downloaded successfully')
      } else {
        const errorText = await response.text()
        console.error('Failed to flatten PDF:', errorText)
        toast.error('Failed to flatten PDF with form field values')
      }
    } catch (error) {
      console.error('Error flattening PDF fields:', error)
      toast.error('Error flattening PDF with form field values')
    }
  }, [data.fileId, data.filename, fieldValues])

  // Handle field value changes
  const handleFieldValueChange = (fieldName: string, value: string) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  // Add handler for immediate local updates
  const handleLocalFieldChange = (fieldName: string, value: string) => {
    setLocalFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  // Add handler for blur events
  const handleFieldBlur = (fieldName: string, value: string) => {
    handleFieldValueChange(fieldName, value)
    // Sync local state with global state
    setLocalFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  // Auto-detect fields when component mounts or fileId changes
  useEffect(() => {
    if (data.fileId && minimizeLevel === 'normal') {
      detectFields()
    }
  }, [data.fileId, minimizeLevel, detectFields])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!isLocked}
      minWidth={500}
      minHeight={400}
      isLocked={isLocked}
      isMinimized={isMinimized}
      minimizeLevel={minimizeLevel}
      nodeTitle={data.label || data.filename || 'PDF Resource'}
      onLockChange={handleLock}
      onMinimizeChange={handleMinimize}
      onMinimizeLevelChange={handleMinimizeLevelChange}
      onTitleChange={handleTitleChange}
      assignedEmails={
        Array.isArray(assignedEmails) 
          ? assignedEmails 
          : []
      }
      onAssignUsersClick={() => {
        const currentEmails = Array.isArray(assignedEmails) 
          ? assignedEmails.join(' ') 
          : (typeof assignedEmails === 'string' ? assignedEmails : '')
        const emailsStr = window.prompt('Enter emails separated by space', currentEmails)
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          setAssignedEmails(emailsArr)
          updateNodeData(id, { ...data, assignedEmails: emailsArr })
        }
      }}
      onDataSettingsClick={() => setIsDataSettingsDialogVisible(true)}
      status={nodeStatus}
      selected={selected}
    >
      <div className={styles.nodeResource} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className={styles.scrollArea} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className={styles.content} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {minimizeLevel === 'normal' && (
              <>
                {/* PDF Viewer Container - Give it more space */}
                <div style={{ flex: 3, minHeight: '300px', display: 'flex', flexDirection: 'column', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
                  {/* PDF Controls */}
                  {pdfUrl && !pdfError && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#fff', borderBottom: '1px solid #dee2e6', flexWrap: 'wrap', minHeight: '48px' }}>
                      <Button variant="outline" size="sm" onClick={goToPrevPage} disabled={pageNumber <= 1}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span style={{ fontSize: '14px', minWidth: '80px', textAlign: 'center' }}>
                        {pageNumber} / {numPages || '?'}
                      </span>
                      <Button variant="outline" size="sm" onClick={goToNextPage} disabled={pageNumber >= numPages}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <div style={{ width: '1px', height: '20px', background: '#dee2e6', margin: '0 4px' }} />
                      <Button variant="outline" size="sm" onClick={zoomOut} disabled={scale <= 0.5}>
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <span style={{ fontSize: '14px', minWidth: '60px', textAlign: 'center' }}>
                        {Math.round(scale * 100)}%
                      </span>
                      <Button variant="outline" size="sm" onClick={zoomIn} disabled={scale >= 3.0}>
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={rotateClockwise}>
                        <RotateCw className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* PDF Content */}
                  <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '16px', minHeight: '250px' }}>
                    {pdfLoading && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}>
                        Loading PDF...
                      </div>
                    )}
                    
                    {/* PDF Viewer */}
                    <div style={{
                      background: '#f8f8f8',
                      border: '1px solid #eee',
                      borderRadius: 4,
                      padding: 8,
                      marginBottom: 8,
                      minHeight: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Loading state for newly created nodes */}
                      {isNewlyCreated && !delayedPdfUrl && (
                        <div style={{ textAlign: 'center', color: '#666' }}>
                          <FileText className="h-16 w-16 mb-4 text-gray-400" />
                          <p style={{ marginBottom: '8px' }}>Preparing PDF...</p>
                          <p style={{ fontSize: '12px', color: '#888' }}>
                            Waiting for file to be ready (Azure Blob Storage processing)
                          </p>
                        </div>
                      )}
                      
                      {/* PDF error state */}
                      {pdfError && delayedPdfUrl && (
                        <div style={{ textAlign: 'center', color: '#666' }}>
                          <FileText className="h-16 w-16 mb-4 text-gray-400" />
                          <p style={{ marginBottom: '16px' }}>{pdfError}</p>
                          <Button onClick={downloadFile} variant="outline">
                            <Download className="mr-2 h-4 w-4" />
                            Download PDF
                          </Button>
                        </div>
                      )}
                      
                      {/* PDF viewer - only show when delayedPdfUrl is available */}
                      {delayedPdfUrl && !pdfError && (
                        <Document
                          file={delayedPdfUrl}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onLoadError={onDocumentLoadError}
                          options={pdfOptions}
                          loading={<div>Loading PDF...</div>}
                          error={<div>Failed to load PDF</div>}
                        >
                          <Page
                            pageNumber={pageNumber}
                            scale={scale}
                            rotate={rotation}
                            onLoadError={onPageLoadError}
                            loading={<div>Loading page...</div>}
                            error={<div>Failed to load page</div>}
                          />
                        </Document>
                      )}
                    </div>
                  </div>
                </div>

                {/* Metadata - Make it more compact */}
                <div className={styles.metaData} style={{ marginTop: '12px', marginBottom: '8px', fontSize: '13px' }}>
                  <span className={styles.metaLabel}>Type:</span>
                  <span className={styles.metaValue}>{data.contentType || 'application/pdf'}</span>

                  <span className={styles.metaLabel}>Size:</span>
                  <span className={styles.metaValue}>{formatFileSize(data.size)}</span>

                  <span className={styles.metaLabel}>Created:</span>
                  <span className={styles.metaValue}>{formatDate(data.createdAt)}</span>

                  {numPages > 0 && (
                    <>
                      <span className={styles.metaLabel}>Pages:</span>
                      <span className={styles.metaValue}>{numPages}</span>
                    </>
                  )}

                  {data.groupName && (
                    <>
                      <span className={styles.metaLabel}>Group:</span>
                      <span className={styles.metaValue}>{data.groupName}</span>
                    </>
                  )}
                </div>

                {/* PDF Form Fields Section */}
                <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>
                      PDF Form Fields
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={detectFields}
                      disabled={isLoadingFields || !data.fileId}
                      style={{ fontSize: '12px', padding: '4px 8px', height: '28px' }}
                    >
                      {isLoadingFields ? 'Detecting...' : 'Detect Fields'}
                    </Button>
                  </div>

                  {isLoadingFields && (
                    <div style={{ color: '#888', fontStyle: 'italic', fontSize: '13px', textAlign: 'center', padding: '16px' }}>
                      Detecting PDF form fields...
                    </div>
                  )}

                  {!isLoadingFields && !hasFields && (
                    <div style={{ color: '#666', fontSize: '13px', textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '4px', border: '1px dashed #dee2e6' }}>
                      No PDF form fields detected
                    </div>
                  )}

                  {!isLoadingFields && hasFields && pdfFields.length > 0 && (
                    <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', padding: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={updatePdfFields}
                            disabled={Object.keys(fieldValues).length === 0}
                            style={{ fontSize: '12px', padding: '4px 12px', height: '28px' }}
                          >
                            Download Filled PDF
                          </Button>
                          <Button
                            variant="outline" 
                            size="sm"
                            onClick={flattenPdfFields}
                            disabled={Object.keys(fieldValues).length === 0}
                            style={{ fontSize: '12px', padding: '4px 12px', height: '28px' }}
                          >
                            Download Flattened PDF
                          </Button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {pdfFields.map((field, index) => (
                          <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                              {field.name}
                            </label>
                            <input
                              type="text"
                              value={localFieldValues[field.name] ?? fieldValues[field.name] ?? ''}
                              onChange={(e) => handleLocalFieldChange(field.name, e.target.value)}
                              onBlur={(e) => handleFieldBlur(field.name, e.target.value)}
                              placeholder={field.value || 'Enter value...'}
                              style={{
                                fontSize: '12px',
                                padding: '6px 8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                background: '#fff'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Markdown Preview - Reduce max height and make it more compact */}
                <div style={{ marginTop: 8, position: 'relative', flex: 1, display: 'flex', minHeight: 0 }}>
                  {isProcessing && (
                    <div style={{ color: '#888', fontStyle: 'italic', fontSize: '13px' }}>
                      {statusMessage || 'Converting PDF to Markdown...'}
                    </div>
                  )}
                  {!isProcessing && data.fileAsMarkdown && (
                    <div style={{ position: 'relative', flex: 1, display: 'flex', minHeight: 0 }}>
                      {/* Copy button in top right corner */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyToClipboard}
                        disabled={!data.fileAsMarkdown}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          padding: 4,
                          height: 28,
                          width: 28,
                        }}
                        title="Copy to clipboard"
                      >
                        {copyFeedback ? copyFeedback : <Copy className="h-3 w-3" />}
                      </Button>
                      <div
                        ref={markdownPreviewRef}
                        style={{
                          background: '#f8f8f8',
                          border: '1px solid #eee',
                          borderRadius: 4,
                          padding: 8,
                          marginTop: 4,
                          flex: 1,
                          minHeight: 0,
                          height: '100%',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          fontFamily: 'monospace',
                          fontSize: 12,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          position: 'relative'
                        }}
                      >
                        {data.fileAsMarkdown}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions - always at bottom */}
        {minimizeLevel === 'normal' && (
          <div
            className={styles.actionButtons}
            style={{
              marginTop: 'auto',
              padding: '0.75rem 1rem 0.5rem 1rem',
              background: 'var(--background, #fff)',
              borderTop: '1px solid #eee',
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadFile}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(data.url, 'URL copied to clipboard')}
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                Copy URL
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={copyMarkdownLink}
              >
                <Clipboard className="mr-2 h-4 w-4" />
                Copy Link
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(data.url, '_blank')}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDataSettingsDialogVisible(true)}
                title="Data Settings"
              >
                <SettingsIcon className="mr-2 h-4 w-4" />
                Data Settings
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={detectFields}
                disabled={isLoadingFields || !data.fileId}
                title="Detect PDF Form Fields"
              >
                <FileText className="mr-2 h-4 w-4" />
                {isLoadingFields ? 'Detecting...' : 'Detect Fields'}
              </Button>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Button
                variant="default"
                size="sm"
                onClick={handleExtractMarkdown}
                disabled={isProcessing || !data.url}
              >
                Extract Data as Text
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleConvertPdfToMd}
                disabled={isProcessing || !data.url}
                style={{ marginLeft: '0.5rem' }}
              >
                PDF to Markdown
              </Button>
            </div>
          </div>
        )}

        {/* Output Handle */}
        <div className={styles.handleWrapper}>
          <Handle 
            type="source" 
            position={Position.Bottom} 
            id="data-output" 
            style={{ 
              background: '#6366F1',
              width: '12px',
              height: '12px'
            }} 
          />
        </div>
      </div>
      {/* Data Settings Dialog */}
      <NodeDataSettingsDialog
        visible={isDataSettingsDialogVisible}
        onHide={() => setIsDataSettingsDialogVisible(false)}
        settings={dataSettings}
        nodeId={id}
        onSettingsChange={handleDataSettingsChange}
        onRemoveInputLink={async () => ({ success: true })}
        onSyncInputLink={async () => ({ success: true })}
        forceShowAvailableNodes={false}
        debug={false}
      />
    </NodeBaseContainer>
  )
}

export default memo(NodeResourcePDF) 